import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ShieldPlus, Trash2, ShieldCheck } from "lucide-react";

const inputCls = "w-full bg-surface border border-ink/20 focus:border-ember outline-none px-3 py-2.5 text-ink placeholder:text-ink/30 transition-colors text-sm";
const labelCls = "block text-[11px] uppercase tracking-[0.2em] text-ink/50 mb-1.5";

export default function AdminAdmins() {
  const [admins, setAdmins] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/admins");
      setAdmins(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/admins", form);
      toast.success("Administrateur créé — il devra changer son mot de passe à la première connexion");
      setForm({ name: "", email: "", password: "" });
      await load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  const remove = async (a) => {
    if (!window.confirm(`Supprimer l'accès administrateur de ${a.email} ?`)) return;
    try {
      await api.delete(`/admin/admins/${a.id}`);
      toast.success("Administrateur supprimé");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="mt-10 grid lg:grid-cols-3 gap-8" data-testid="admin-admins">
      <div className="lg:col-span-2 border border-ink/10 bg-surface">
        <div className="px-6 py-4 border-b border-ink/10 text-[11px] uppercase tracking-[0.2em] text-ink/40 grid grid-cols-12 gap-4">
          <span className="col-span-6">Administrateur</span>
          <span className="col-span-3">Statut</span>
          <span className="col-span-3 text-right">Actions</span>
        </div>
        {admins.map((a) => (
          <div key={a.id} className="grid grid-cols-12 gap-4 items-center px-6 py-4 border-b border-ink/5" data-testid={`admin-row-${a.id}`}>
            <div className="col-span-6 min-w-0">
              <p className="text-sm font-medium text-ink flex items-center gap-2 truncate">
                <ShieldCheck size={14} className="text-ember shrink-0" /> {a.name || "—"}
                {a.is_self && <span className="text-[10px] uppercase tracking-wider text-ink/35">(vous)</span>}
              </p>
              <p className="text-xs text-ink/45 truncate">{a.email}</p>
            </div>
            <p className="col-span-3 text-xs">
              {a.is_main
                ? <span className="text-ember font-medium">Principal</span>
                : a.must_change_password
                  ? <span className="text-amber-600">Doit changer son mot de passe</span>
                  : <span className="text-green-600">Actif</span>}
            </p>
            <div className="col-span-3 flex justify-end">
              {!a.is_main && !a.is_self && (
                <button onClick={() => remove(a)} data-testid={`admin-delete-${a.id}`} className="p-2 border border-ink/15 text-ink/50 hover:text-red-500 hover:border-red-300 transition-colors">
                  <Trash2 size={13} />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <form onSubmit={create} className="border border-ink/10 bg-surface p-6 h-fit" data-testid="admin-create-admin-form">
        <h3 className="font-display font-medium text-ink flex items-center gap-2"><ShieldPlus size={16} className="text-ember" /> Ajouter un administrateur</h3>
        <p className="mt-2 text-xs text-ink/50 leading-relaxed">
          Le nouvel administrateur recevra un email de bienvenue et devra obligatoirement choisir
          son propre mot de passe à sa première connexion.
        </p>
        <div className="mt-5 space-y-4">
          <div><label className={labelCls}>Nom complet</label><input data-testid="new-admin-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className={inputCls} /></div>
          <div><label className={labelCls}>Email</label><input data-testid="new-admin-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className={inputCls} /></div>
          <div><label className={labelCls}>Mot de passe par défaut</label><input data-testid="new-admin-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={6} className={inputCls} /></div>
          <button data-testid="new-admin-submit" disabled={busy} className="btn-glow w-full bg-ink hover:bg-ember disabled:opacity-50 text-white px-4 py-3 text-sm transition-colors">
            Créer l'administrateur
          </button>
        </div>
      </form>
    </div>
  );
}
